import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { MdCheck, MdClose } from "react-icons/md";

const ConfirmationDrawer = ({
  showConfirmation,
  handleBackToForm,
  selectedPlan,
  formData,
  handleFinalSubmit,
  isPendingUpdate,
  isPending,
  privilegeOptions,
  getFeeById,
  formatFeeName,
  getPaystackPlanLabel,
}) => {
  const resolvedAnnualPrice =
    formData.annualPrice !== "" &&
    formData.annualPrice !== null &&
    formData.annualPrice !== undefined
      ? parseFloat(formData.annualPrice)
      : (parseFloat(formData.price) || 0) * 11;

  return (
    <Drawer
      anchor="right"
      open={showConfirmation}
      onClose={handleBackToForm}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 520 } },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" className="font-semibold">
            Confirm Plan Details
          </Typography>
          <IconButton onClick={handleBackToForm} size="small">
            <MdClose />
          </IconButton>
        </Box>

        {/* Confirmation Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {/* Confirmation Message */}
          <Paper
            sx={{
              p: 3,
              bgcolor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: "12px",
              mb: 3,
            }}
          >
            <Typography variant="body2" className="text-slate-700">
              Please review the plan details before{" "}
              {selectedPlan ? "updating" : "creating"}. You can go back to make
              changes if needed.
            </Typography>
          </Paper>

          <Box className="space-y-4">
            {/* Basic Info */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Basic Information
              </Typography>
              <Box className="space-y-2">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Plan Name
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.name || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Mode
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900 capitalize"
                  >
                    {formData.mode || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Paystack ID
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {getPaystackPlanLabel
                      ? getPaystackPlanLabel(formData.paystackId)
                      : formData.paystackId || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Annual Plan ID
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {getPaystackPlanLabel
                      ? getPaystackPlanLabel(formData.annualPlanId)
                      : formData.annualPlanId || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900 text-right"
                    sx={{ maxWidth: "60%" }}
                  >
                    {formData.description || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Pricing */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Pricing
              </Typography>
              <Box className="space-y-2">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Price
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.price
                      ? `₦${parseFloat(formData.price).toLocaleString()}`
                      : "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Annual Price
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {resolvedAnnualPrice
                      ? `₦${resolvedAnnualPrice.toLocaleString()}`
                      : "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Initial Setup Fee
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.initialSetupFee
                      ? `₦${parseFloat(formData.initialSetupFee).toLocaleString()}`
                      : "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Duration (Days)
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.durationInDays || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Color
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {formData.color && (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "4px",
                          bgcolor: `#${formData.color}`,
                          border: "1px solid #E2E8F0",
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      className="font-medium text-slate-900"
                    >
                      {formData.color ? `#${formData.color}` : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Features */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Features
              </Typography>
              <Box className="space-y-2">
                {formData.features && formData.features.length > 0 ? (
                  formData.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1.5,
                        borderBottom: "1px solid #F1F5F9",
                      }}
                    >
                      <Typography
                        variant="body2"
                        className="text-slate-600"
                        sx={{ maxWidth: "80%" }}
                      >
                        {feature}
                      </Typography>
                      <Chip
                        label="Included"
                        size="small"
                        sx={{
                          bgcolor: "#DCFCE7",
                          color: "#16A34A",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" className="text-slate-500 italic">
                    No features selected
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Privileges */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Privileges
              </Typography>
              <Box className="space-y-2">
                {formData.privileges && formData.privileges.length > 0 ? (
                  formData.privileges.map((privilege, index) => {
                    const privilegeLabel =
                      privilegeOptions.find((opt) => opt.value === privilege)
                        ?.label || privilege;
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1.5,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        <Typography
                          variant="body2"
                          className="text-slate-600"
                          sx={{ maxWidth: "80%" }}
                        >
                          {privilegeLabel}
                        </Typography>
                        <Chip
                          label="Granted"
                          size="small"
                          sx={{
                            bgcolor: "#DCFCE7",
                            color: "#16A34A",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" className="text-slate-500 italic">
                    No privileges selected
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Selected Fees */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Selected Fees ({formData.fees?.length || 0})
              </Typography>
              <Box className="space-y-2">
                {formData.fees && formData.fees.length > 0 ? (
                  formData.fees.map((feeId, index) => {
                    const fee = getFeeById(feeId);
                    if (!fee) {
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.5,
                            borderBottom: "1px solid #F1F5F9",
                          }}
                        >
                          <Typography
                            variant="body2"
                            className="text-slate-600"
                          >
                            Fee ID: {feeId}
                          </Typography>
                          <Chip
                            label="Selected"
                            size="small"
                            sx={{
                              bgcolor: "#DCFCE7",
                              color: "#16A34A",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.5,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            className="text-slate-600"
                          >
                            {formatFeeName(fee)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            {fee.minimumFlightIssued !== undefined && (
                              <Chip
                                label={`Min: ${fee.minimumFlightIssued}`}
                                size="small"
                                sx={{
                                  bgcolor: "#EFF6FF",
                                  color: "#1F4FFF",
                                  height: 20,
                                  fontSize: "0.7rem",
                                }}
                              />
                            )}
                            {fee.maximumFlightIssued !== undefined && (
                              <Chip
                                label={`Max: ${fee.maximumFlightIssued}`}
                                size="small"
                                sx={{
                                  bgcolor: "#EFF6FF",
                                  color: "#1F4FFF",
                                  height: 20,
                                  fontSize: "0.7rem",
                                }}
                              />
                            )}
                            {fee.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{
                                  bgcolor: "#FEF3C7",
                                  color: "#D97706",
                                  height: 20,
                                  fontSize: "0.7rem",
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Chip
                          label="Selected"
                          size="small"
                          sx={{
                            bgcolor: "#DCFCE7",
                            color: "#16A34A",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" className="text-slate-500 italic">
                    No fees selected
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Additional Settings */}
            <Box>
              <Typography
                variant="subtitle2"
                className="font-semibold text-slate-900 mb-3"
              >
                Additional Settings
              </Typography>
              <Box className="space-y-2">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Grace Period
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.gracePeriodDays || 0} days
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Trial Duration
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.trialDurationDays || 0} days
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Earn to Pay Method
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900 capitalize"
                  >
                    {formData.earnToPayMethod || "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Earn to Pay Amount
                  </Typography>
                  <Typography
                    variant="body2"
                    className="font-medium text-slate-900"
                  >
                    {formData.earnToPayAmount
                      ? `₦${parseFloat(formData.earnToPayAmount).toLocaleString()}`
                      : "-"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="body2" className="text-slate-600">
                    Status
                  </Typography>
                  <Chip
                    label={formData.isActive ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      bgcolor: formData.isActive ? "#DCFCE7" : "#F1F5F9",
                      color: formData.isActive ? "#16A34A" : "#64748B",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            gap: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToForm}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              borderColor: "#E2E8F0",
              color: "#475569",
            }}
          >
            Back to Edit
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<MdCheck />}
            sx={{
              textTransform: "none",
              bgcolor: "#1F4FFF",
              borderRadius: "10px",
              "&:hover": {
                bgcolor: "#1a40d9",
              },
            }}
            onClick={handleFinalSubmit}
            disabled={isPendingUpdate || isPending}
          >
            {selectedPlan
              ? isPendingUpdate
                ? "Updating..."
                : "Confirm & Update"
              : isPending
                ? "Creating..."
                : "Confirm & Create"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConfirmationDrawer;
